import { useCallback } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { getCloudId } from '@helpcenter/meta';
import { di } from 'react-magnetic-di';
import { useService } from '@atlassian/help-center-common-util/use-service';
import { usePfErrorUtils } from '@atlassian/proforma-common-core/error-utils-context';
import type { ApiFieldDataResponse } from '@atlassian/proforma-form-renderer/field-data';

import {
	useRequestTypeId,
	useServiceDeskId,
} from '../../controllers/request-create-context-provider';
import { proformaGasAnalyticsFetch } from '../../common/services/proforma-gas-analytics-fetch';
import { PROFORMA_STARGATE_URL_PREFIX } from '../constants';
import type { FieldDataRequestProps } from './types';

/**
 * Fetches field data from the ProForma Stargate backend.
 *
 * Uses serviceDeskId because this calls the ProForma backend API which passes
 * the value to JSM REST APIs that only accept serviceDeskId.
 * portalId and serviceDeskId are NOT interchangeable.
 *
 * See docs/PORTAL_ID_AND_SERVICE_DESK_ID.md for the distinction.
 */
export const useGetFieldDataService = () => {
	di(useServiceDeskId, useRequestTypeId);

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const errorUtils = usePfErrorUtils();
	const cloudId = getCloudId();
	const serviceDeskId = useServiceDeskId();
	const requestTypeId = useRequestTypeId();

	const request = useCallback(
		(requestData: FieldDataRequestProps) =>
			proformaGasAnalyticsFetch<ApiFieldDataResponse>(
				`${PROFORMA_STARGATE_URL_PREFIX}/${cloudId}/api/cloud/portal/${serviceDeskId}/requesttype/${requestTypeId}/form/${requestData.templateFormId}/fielddata`,
				{
					method: 'POST',
					body: JSON.stringify(requestData.fieldDataRequest),
				},
				createAnalyticsEvent,
				errorUtils,
			),
		[cloudId, serviceDeskId, requestTypeId, createAnalyticsEvent, errorUtils],
	);

	return useService(request);
};
